import axios from 'axios'

import type { AxiosPromise, AxiosRequestConfig } from 'axios';

/**
 * Defines an interceptor for modifying request configurations when using
 * related functions.
 *
 * @see request
 * @see authenticatedRequest
 * @see useRequestor In rest.hooks.ts
 * @see useAuthenticatedRequestor In rest.hooks.ts
 */
export type RequestConfigInterceptor = (
  /** The request configuration to be intercepted and modified. */
  request: AxiosRequestConfig,
) => AxiosRequestConfig

/**
 * Defines a request function.
 */
export type Request<ResponseData extends object> = (
  /** The base URL to perform the request against. */
  apiUrlBase: string,
  /** Optional interceptor to change the request config before being sent. */
  interceptor?: RequestConfigInterceptor,
) => AxiosPromise<ResponseData>

export type AuthenticatedRequest<ResponseData extends object> = (
  /** The base URL to perform the request against. */
  apiUrlBase: string,
  /** The API token to use for authentication. */
  apiToken: string,
  /** Optional interceptor to change the request config before being sent. */
  interceptor?: RequestConfigInterceptor,
) => AxiosPromise<ResponseData>

/**
 * Generates a request function that will send the prepared request when
 * executed given the `apiUrlBase`.
 *
 * @see authenticatedRequest For performing authenticated requests.
 * @see useRequestor In rest.hooks.ts for easily executing requests in React
 *      components with the configured `apiUrlBase`.
 *
 * @example
 * // Define the request
 * function getUsers(adminsOnly: boolean) {
 *   return request<{ users: User[] }>({
 *     method: 'get',
 *     url: '/users',
 *     data: { adminsOnly }
 *   })
 * }
 *
 * // When the request function is executed:
 * // GET http://host:12345/api/users
 * // { "adminsOnly": true }
 * try {
 *   const response = await getUsers(true)('http://host:12345/api')
 *   console.log(response.data.users)
 * } catch (e) {
 *   // Note that catch variables cannot be typed.  Errors stemming from REST
 *   // requests will conform to the `AxiosError` interface.
 *   console.log(e.response.status)
 * }
 *
 * @param requestConfig The `AxiosRequestConfig` for the request.  Note that the
 *        `url`, and potentially any other properties can be modified later by
 *        the `interceptor` on the returned function.
 * @returns A request function that takes the `apiUrlBase` an optional
 *          `interceptor` which can be used to modify the `AxiosRequestConfig`
 *          before being sent.  The `apiUrlBase` will be prepended to the `url`
 *          provided by `requestConfig`.
 */
export function request<ResponseData extends object>(
  requestConfig: AxiosRequestConfig,
): Request<ResponseData> {
  return (apiUrlBase, interceptor = (r) => r) =>
    Promise.resolve(requestConfig)
      .then((r) => ({ ...r, url: `${apiUrlBase}${r.url}` }))
      .then(interceptor)
      .then((r) => axios(r))
}

/**
 * Generates a request function that will send the prepared request when
 * executed given the `apiUrlBase` and `apiToken`.
 *
 * @see request For performing unauthenticated requests.
 * @see useAuthenticatedRequestor In rest.hooks.ts for easily executing requests
 *      in React components with the configured `apiUrlBase` and `apiToken`.
 *
 * @example
 * // Define the request
 * function getProcedure(procedureId: IProcedureId) {
 *   return authenticatedRequest<IProcedure>(Promise.resolve({
 *     method: 'get',
 *     url: `/procedure/${procedureId}`,
 *   }))
 * }
 *
 * // When the request function is executed:
 * // GET http://host:12345/api/procedure/3220
 * // X-Auth-Token: TOKEN999
 * try {
 *   const response = await getProcedure('3220')('http://host:12345/api', 'TOKEN999')
 *   console.log(response.data.id)
 * } catch (e) {
 *   // Note that catch variables cannot be typed.  Errors stemming from REST
 *   // requests will conform to the `AxiosError` interface.
 *   console.log(e.response.status)
 * }
 *
 * @param requestConfig The `AxiosRequestConfig` for the request.  Note that the
 *        `url`, the `Authorization` header, and potentially any other
 *        properties can be modified later by the `interceptor` on the returned
 *        function.
 * @returns A request function that takes the `apiUrlBase`, the `apiToken`, and
 *          an optional `interceptor` which can be used to modify the
 *          `AxiosRequestConfig` before being sent.  The `apiUrlBase` will be
 *          prepended to the `url` provided by `requestConfig`.
 */
export function authenticatedRequest<ResponseData extends object>(
  requestConfig: AxiosRequestConfig,
): AuthenticatedRequest<ResponseData> {
  return (apiUrlBase, apiToken, interceptor = (r) => r) =>
    Promise.resolve(requestConfig)
      .then((r) => ({
        ...r,
        url: `${apiUrlBase}${r.url}`,
        headers: {
          ...(r.headers ?? {}),
          'X-Auth-Token': `${apiToken}`,
        },
      }))
      .then(interceptor)
      .then((r) => axios(r))
}

export function shallowObjectToQueryParams(o: {
  [key: string]: unknown
}): string {
  return Object.keys(o)
    .map((k) => `${escape(k)}=${escape(`${o[k]}`)}`)
    .join('&')
}
